import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../components/Spinner";
import profileService, { UserProfileResponse } from "../services/profileService";
import planDetailsService, {
  PlanDetailsResponse,
  PlanSportRow,
  PlanAddonRow,
} from "../services/planDetailsService";
import { SUBSCRIPTION, ADDONS } from "../constants/routes";
import localStorageService from "../services/localStorageService";

// ─── helpers ─────────────────────────────────────────────────────────────────

function getSportCode(row: PlanSportRow): string {
  return String(row?.sport_code || row?.sportCode || row?.sport || "");
}

function getAddonName(row: PlanAddonRow): string {
  return String(row?.addon_name || row?.addonName || row?.addon || row?.name || "");
}

function isSubscribedSport(row: PlanSportRow): boolean {
  const status = String(row?.status || "").toLowerCase();
  return status === "subscribed" || status === "active" || status === "paid";
}

function isTrialSport(row: PlanSportRow): boolean {
  const status = String(row?.status || "").toLowerCase();
  return status === "trial" || status === "in_trial" || status === "in-trial";
}

function isActiveAddon(row: PlanAddonRow): boolean {
  if (row?.active === true) return true;
  const status = String(row?.status || "").toLowerCase();
  return status === "active" || status === "subscribed" || status === "enabled";
}

function isGraceAccessRow(row: any): boolean {
  return row?.is_grace === true || row?.isGrace === true;
}

function getGraceEnd(row: any): string {
  return String(row?.grace_end || row?.graceEnd || row?.grace_end_date || row?.graceEndDate || "");
}

function getTrialEnd(row: any): string {
  return String(row?.trial_end || row?.trialEnd || "");
}

function asNumber(value: any): number | null {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function getSelectedSeasons(row: PlanAddonRow): string[] {
  if (row?.selected_seasons && Array.isArray(row.selected_seasons)) return row.selected_seasons;
  if (row?.selectedSeasons && Array.isArray(row.selectedSeasons)) return row.selectedSeasons;
  return [];
}

function splitPayload(payload: PlanDetailsResponse | null): {
  sports: PlanSportRow[];
  addons: PlanAddonRow[];
} {
  const sports = (payload?.sports || payload?.data?.sports || []) as PlanSportRow[];
  const allAddons: PlanAddonRow[] = [];
  sports.forEach((sport) => {
    const sportAddons = (sport as any)?.active_addons || [];
    sportAddons.forEach((a: PlanAddonRow) => {
      allAddons.push({ ...a, sport_code: sport.sport_code });
    });
  });
  return { sports, addons: allAddons };
}

function getDisplayName(user: any): string {
  if (user?.full_name) return String(user.full_name);
  if (user?.fullName) return String(user.fullName);
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (user?.name) return String(user.name);
  if (user?.username) return String(user.username);
  return 'User';
}

function formatDate(dateLike?: string) {
  if (!dateLike) return 'Not available';
  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── sport icon map ──────────────────────────────────────────────────────────

const SPORT_CODE_TO_SUBSCRIPTION_ID: Record<string, string> = {
  MFB: 'football',
  MBB: 'basketball-m',
  WBB: 'basketball-w',
  MIH: 'ice-hockey-m',
  WIH: 'ice-hockey-w',
  MSO: 'soccer-m',
  WSO: 'soccer-w',
  MVB: 'volleyball-m',
  WVB: 'volleyball-w',
};

const SPORT_EMOJI: Record<string, string> = {
  football: "🏈",
  basketball: "🏀",
  volleyball: "🏐",
  baseball: "⚾",
  soccer: "⚽",
  tennis: "🎾",
  hockey: "🏒",
  lacrosse: "🥍",
  softball: "🥎",
  track: "🏃",
  swimming: "🏊",
  golf: "⛳",
  wrestling: "🤼",
  gymnastics: "🤸",
};

function sportEmoji(code: string): string {
  // First convert sport code (MFB) to subscription ID (football) if needed
  const subId = SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase();
  const key = subId.toLowerCase().replace(/[^a-z]/g, "");
  for (const [k, v] of Object.entries(SPORT_EMOJI)) {
    if (key.includes(k)) return v;
  }
  return "🏅";
}

function sportGenderBadge(code: string): { label: string; type: "M" | "W" } | null {
  const upper = code.toUpperCase();
  if (upper.includes(" W") || upper.endsWith("_W") || upper.includes("WOMEN")) return { label: "W", type: "W" };
  if (upper.includes(" M") || upper.endsWith("_M") || upper.includes("MEN")) return { label: "M", type: "M" };
  return null;
}

// ─── sub-components ──────────────────────────────────────────────────────────

function GenderBadge({ type }: { type: "M" | "W" }) {
  const style: React.CSSProperties =
    type === "M"
      ? { background: "#E6F1FB", color: "#185FA5" }
      : { background: "#FBEAF0", color: "#993556" };
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        padding: "1px 5px",
        borderRadius: 3,
        marginLeft: 4,
        ...style,
      }}
    >
      {type}
    </span>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export default function Profile() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [orgName, setOrgName] = useState("");
  const [expandedAddons, setExpandedAddons] = useState<Record<string, boolean>>({});

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);

  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState<string | null>(null);
  const [planPayload, setPlanPayload] = useState<PlanDetailsResponse | null>(null);

  // Get username and orgName from localStorage
  useEffect(() => {
    try {
      const parsed = localStorageService.getUserInfo() || {};
      setUsername(parsed?.username || "");
      setEmail(parsed?.email || "");
      setOrgName(parsed?.team?.name || parsed?.team_name || parsed?.organization_name || "Team");
    } catch {
      setUsername("");
      setEmail("");
      setOrgName("Team");
    }
  }, []);

  // Load profile (same pattern as ViewProfileModal)
  useEffect(() => {
    if (!username) return;
    let alive = true;
    async function load() {
      setProfileLoading(true);
      setProfileError(null);
      const res = await profileService.getProfile(username, email);
      if (!alive) return;
      if (res?.status >= 200 && res?.status < 300) {
        setProfile(res.data);
      } else {
        setProfileError(
          String(res?.data?.detail?.message || res?.data?.detail || res?.data?.message || "Failed to load profile.")
        );
      }
      setProfileLoading(false);
    }
    void load();
    return () => { alive = false; };
  }, [email, username]);

  // Load plan details (same pattern as PlanDetailsModal)
  useEffect(() => {
    if (!username) return;
    let alive = true;
    async function load() {
      setPlanLoading(true);
      setPlanError(null);
      const res = await planDetailsService.getPlanDetails(username);
      if (!alive) return;
      if (res?.status >= 200 && res?.status < 300) {
        setPlanPayload(res.data);
      } else {
        setPlanError(
          String(res?.data?.detail?.message || res?.data?.detail || res?.data?.message || "Failed to load plan details.")
        );
      }
      setPlanLoading(false);
    }
    void load();
    return () => { alive = false; };
  }, [username]);

  const { sports, addons } = useMemo(() => splitPayload(planPayload), [planPayload]);
  const subscribedSports = useMemo(() => sports.filter(isSubscribedSport), [sports]);
  const trialSports = useMemo(() => sports.filter(isTrialSport), [sports]);
  const activeAddons = useMemo(() => addons.filter(isActiveAddon), [addons]);
  const graceSports = useMemo(
    () => subscribedSports.filter((s) => isGraceAccessRow(s) && Boolean(getGraceEnd(s))),
    [subscribedSports]
  );
  const nonGraceSubscribedSports = useMemo(
    () => subscribedSports.filter((s) => !isGraceAccessRow(s)),
    [subscribedSports]
  );

  // Group addons by sport so multiple seasons for same sport appear in one box
  const groupedAddonsBySport = useMemo(() => {
    const grouped: Record<string, {
      sportCode: string;
      sportName: string;
      emoji: string;
      addonName: string;
      costPerSeason: number;
      seasons: { year: string; expiry?: string }[];
      graceSeasons: { year: string }[];
      total: number;
      isGrace?: boolean;
      graceEnd?: string;
    }> = {};

    activeAddons.forEach((addon) => {
      const sportCode = addon?.sport_code || "";
      const subId = SPORT_CODE_TO_SUBSCRIPTION_ID[sportCode] || sportCode.toLowerCase();
      const key = `${subId}-${getAddonName(addon)}`;

      if (!grouped[key]) {
        const sportLabel = subId.replace(/-m$/i, " (Men)").replace(/-w$/i, " (Women)").replace(/([A-Z])/g, " $1").trim();
        const sportLabelCapitalized = sportLabel.charAt(0).toUpperCase() + sportLabel.slice(1);
        const costPerSeason = asNumber(addon?.cost_per_season ?? addon?.cost) ?? 0;

        grouped[key] = {
          sportCode,
          sportName: sportLabelCapitalized,
          emoji: sportEmoji(sportCode),
          addonName: getAddonName(addon),
          costPerSeason,
          seasons: [],
          graceSeasons: [],
          total: 0,
          isGrace: false,
          graceEnd: "",
        };
      }

      const seasons = getSelectedSeasons(addon);
      const total = asNumber(addon?.total_cost ?? addon?.totalCost ?? addon?.amount) ?? 0;

      if (isGraceAccessRow(addon)) {
        grouped[key].isGrace = true;
        const graceEnd = getGraceEnd(addon);
        if (graceEnd) {
          // Keep the latest grace end date if we see multiple rows.
          const current = grouped[key].graceEnd || "";
          grouped[key].graceEnd = !current || new Date(graceEnd) > new Date(current) ? graceEnd : current;
        }
        seasons.forEach((year) => {
          grouped[key].graceSeasons.push({ year: String(year) });
        });
      } else {
        seasons.forEach((year) => {
          grouped[key].seasons.push({ year: String(year), expiry: "Season expire" });
        });
        grouped[key].total += total;
      }
    });

    // Remove duplicates from seasons and sort by year
    Object.values(grouped).forEach((g) => {
      const unique = new Map();
      g.seasons.forEach((s) => unique.set(s.year, s));
      g.seasons = Array.from(unique.values()).sort((a, b) => b.year.localeCompare(a.year));

      const uniqueGrace = new Map<string, { year: string }>();
      g.graceSeasons.forEach((s) => uniqueGrace.set(s.year, s));
      g.graceSeasons = Array.from(uniqueGrace.values()).sort((a, b) => b.year.localeCompare(a.year));
    });

    return grouped;
  }, [activeAddons]);

  const toggleAddonExpanded = (key: string) => {
    setExpandedAddons((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getVisibleSeasons = (g: typeof groupedAddonsBySport[string], key: string) => {
    const isExpanded = expandedAddons[key] || false;
    if (isExpanded || g.seasons.length <= 2) return g.seasons;
    return g.seasons.slice(0, 2);
  };

  const displayName = getDisplayName(profile || {});
  const displayEmail = profile?.email || email || "";
  const displayDesignation = profile?.designation || "";
  const emailOrUsername = profile?.email || username || '';

  const totalSportCost = useMemo(() => {
    const costs = subscribedSports.map((s) => {
      const sportCost = s?.sport_cost;
      if (typeof sportCost === 'object' && sportCost !== null && 'amount' in sportCost) {
        return asNumber(sportCost.amount) ?? 0;
      }
      return asNumber(sportCost ?? s?.cost ?? s?.price ?? s?.amount) ?? 0;
    });
    return costs.reduce((a, b) => a + b, 0);
  }, [subscribedSports]);

  const totalAddonCost = useMemo(() => {
    return activeAddons.reduce((sum, a) => {
      const total = asNumber(a?.total_cost ?? a?.totalCost ?? a?.amount) ?? 0;
      return sum + total;
    }, 0);
  }, [activeAddons]);

// ✅ Group addons by sport
const addonsBySport = useMemo(() => {
  const grouped: Record<string, PlanAddonRow[]> = {};

  activeAddons.forEach((addon) => {
    const sport = addon.sport_code || "UNKNOWN";

    if (!grouped[sport]) {
      grouped[sport] = [];
    }

    grouped[sport].push(addon);
  });

  return grouped;
}, [activeAddons]);

// ✅ Total per sport
const addonTotalsBySport = useMemo(() => {
  const result: Record<string, number> = {};

  Object.entries(addonsBySport).forEach(([sport, addons]) => {
    const total = addons.reduce((sum, addon) => {
      return sum + (addon.total_cost || 0);
    }, 0);

    result[sport] = total;
  });

  return result;
}, [addonsBySport]);

// ✅ Grand total
const grandTotal = useMemo(() => {
  return Object.values(addonTotalsBySport).reduce((a, b) => a + b, 0);
}, [addonTotalsBySport]);

  const renewalDate = useMemo(() => {
    const raw = (planPayload as any)?.renewal_date || (planPayload as any)?.renewalDate || "";
    return formatDate(raw) || "-";
  }, [planPayload]);

  function formatDateYYYYMMDD(dateLike?: string) {
  if (!dateLike) return "Not available";

  const date = new Date(dateLike);
  if (Number.isNaN(date.getTime())) return "Not available";

  return date.toISOString().split("T")[0];
}

console.log("Profile data:", planPayload);

  const isLoading = profileLoading || planLoading;

  return (
    <div className="min-h-screen w-full bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.98),_rgba(236,242,252,0.92)_38%,_rgba(220,229,245,0.88)_100%)] px-4 py-10">
      <div className="mx-auto max-w-5xl">
        {/* Combined Profile & Premium Card */}
        <div className="mb-8 max-w-4xl mx-auto rounded-[28px] border border-slate-200/80 bg-white/95 p-8 shadow-[0_25px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl hover:shadow-[0_35px_100px_rgba(15,23,42,0.15)] transition-all duration-300">
          {/* Header Row */}
          <div className="flex flex-col lg:flex-row lg:items-start lg:gap-12 mb-8">
            
            {/* Profile Section - Left */}
            <div className="flex-1 mb-8 lg:mb-0">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-slate-100 to-slate-200 text-slate-600 shadow-sm">
                  <svg className="h-10 w-10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                    <path d="M20 21a8 8 0 10-16 0" />
                    <circle cx="12" cy="8" r="4" />
                  </svg>
                </div>
                <div>
	                  <h1 className="text-2xl font-bold text-slate-900 leading-tight">{displayName}</h1>
	                  <p className="text-sm text-slate-600 mt-1">{displayEmail || '-'}</p>
	                </div>
	              </div>
              
              {/* Profile Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Designation</p>
                  <p className="font-semibold text-slate-900">{displayDesignation || '-'}</p>
                </div>
	                <div>
	                  <p className="text-xs text-slate-500 uppercase tracking-wider font-medium mb-1">Full name</p>
	                  <p className="font-semibold text-slate-900">{profile?.full_name || '-'}</p>
	                </div>
              </div>
            </div>

            {isLoading ? (
              <div className="flex flex-1 items-center justify-center self-stretch min-h-[220px] w-full max-w-md mx-auto lg:mx-0">
                <Spinner size="lg" />
              </div>
            ) : (
            <div className="flex-1 max-w-md mx-auto lg:mx-0">
              {/* Premium Badge - Centered */}
              <div className="flex items-center justify-center gap-3 mb-8 p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl shadow-sm">
                <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full shadow-sm animate-pulse"></div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 via-slate-800 to-emerald-800 bg-clip-text text-transparent tracking-tight">
                  Premium Plan
                </h2>
              </div>
              
              <div className="flex flex-col sm:flex-row items-start sm:items-end gap-8 mb-8 px-4">
              {/* Sports */}
              <div className="text-center flex-1">
                <p className="text-xs font-semibold uppercase text-slate-600 tracking-wider mb-3">Sports</p>
                <div className="text-3xl font-black text-slate-900 mb-2">${totalSportCost}</div>
              </div>
              
              {/* Addons */}
              <div className="text-center flex-1">
                <p className="text-xs font-semibold uppercase text-slate-600 tracking-wider mb-3">Add-ons</p>
                <div className="text-3xl font-black text-slate-900 mb-2">${totalAddonCost}</div>
              </div>
              
              {/* Total */}
              <div className="text-center flex-1">
                <p className="text-xs font-bold uppercase text-slate-700 tracking-wider mb-2">Total</p>
                <div className="text-4xl font-bold text-slate-900">${totalSportCost + totalAddonCost}</div>
              </div>
            </div>
                        </div>
            )}

            {/* Premium Section - Right (Centered) */}


          </div>
        </div>








        {/* Sports and Add-ons Grid */}
        <div className="grid gap-6 md:grid-cols-2">
          {/* Subscribed Sports */}
          <div className="rounded-[28px] border border-slate-200 bg-white/95 p-6 shadow-[0_18px_60px_rgba(15,23,42,0.10)] backdrop-blur overflow-y-auto max-h-[600px]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900">Subscribed Sports ({subscribedSports.length})</h2>
              <button
                type="button"
                onClick={() => navigate(SUBSCRIPTION)}
                disabled={isLoading}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-red-50"
              >
                Add a Sport
              </button>

            </div>

            {isLoading ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-2xl bg-slate-50 px-4 py-6">
                <Spinner size="md" />
              </div>
            ) : (
              <div className="space-y-3">
	                {graceSports.length > 0 && (
	                  <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-extrabold uppercase tracking-wider text-amber-800">
                        Grace Access
                      </span>
                      <span className="text-[11px] font-semibold text-amber-700">
                        Access until
                      </span>
                    </div>
                    <div className="space-y-2">
                      {graceSports.map((sport) => {
                        const code = getSportCode(sport);
                        const subId = SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase();
                        const badge = sportGenderBadge(code);
                        const label = subId
                          .replace(/-m$/i, " (M)")
                          .replace(/-w$/i, " (W)")
                          .replace(/([A-Z])/g, " $1")
                          .trim();
                        const labelCapitalized = label.charAt(0).toUpperCase() + label.slice(1);
                        const graceEnd = formatDateYYYYMMDD(getGraceEnd(sport));
                        return (
                          <div key={`grace-${code}`} className="flex items-center justify-between rounded-xl bg-white/70 px-3 py-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className="text-base">{sportEmoji(code)}</span>
                              <span className="text-sm font-semibold text-slate-900 truncate">
                                {labelCapitalized || code}
                                {badge && <GenderBadge type={badge.type} />}
                              </span>
                            </div>
                            <span className="text-sm font-semibold text-amber-900">{graceEnd}</span>
                          </div>
                        );
                      })}
                    </div>
	                  </div>
	                )}
	                {trialSports.length > 0 && (
	                  <div className="rounded-2xl border border-sky-200 bg-sky-50/60 px-4 py-3">
	                    <div className="mb-2 flex items-center justify-between">
	                      <span className="text-xs font-extrabold uppercase tracking-wider text-sky-800">
	                        Trial
	                      </span>
	                      <span className="text-[11px] font-semibold text-sky-700">
	                        Access until
	                      </span>
	                    </div>
	                    <div className="space-y-2">
	                      {trialSports.map((sport) => {
	                        const code = getSportCode(sport);
	                        const subId = SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase();
	                        const badge = sportGenderBadge(code);
	                        const label = subId
	                          .replace(/-m$/i, " (M)")
	                          .replace(/-w$/i, " (W)")
	                          .replace(/([A-Z])/g, " $1")
	                          .trim();
	                        const labelCapitalized = label.charAt(0).toUpperCase() + label.slice(1);
	                        const trialEnd = formatDateYYYYMMDD(getTrialEnd(sport));
	                        return (
	                          <div key={`trial-${code}`} className="flex items-center justify-between rounded-xl bg-white/70 px-3 py-2">
	                            <div className="flex items-center gap-2 min-w-0">
	                              <span className="text-base">{sportEmoji(code)}</span>
	                              <span className="text-sm font-semibold text-slate-900 truncate">
	                                {labelCapitalized || code}
	                                {badge && <GenderBadge type={badge.type} />}
	                              </span>
	                            </div>
	                            <span className="text-sm font-semibold text-sky-900">{trialEnd}</span>
	                          </div>
	                        );
	                      })}
	                    </div>
	                  </div>
	                )}
	                {nonGraceSubscribedSports.length > 0 ? (
	                  nonGraceSubscribedSports.map((sport) => {
	                    const code = getSportCode(sport);
	                    const subId = SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase();
	                    const amount = asNumber(sport?.sport_cost ?? sport?.cost ?? sport?.price ?? sport?.amount);
	                    const subscriptionEnd = formatDateYYYYMMDD(
	                      String((sport as any)?.subscription_end || (sport as any)?.subscriptionEnd || "")
	                    );
	                    const badge = sportGenderBadge(code);
	                    const label = subId.replace(/-m$/i, " (M)").replace(/-w$/i, " (W)").replace(/([A-Z])/g, " $1").trim();
	                    const labelCapitalized = label.charAt(0).toUpperCase() + label.slice(1);

                    return (
                      <div
                        key={code}
                        className="flex items-center justify-between rounded-2xl bg-slate-50 px-4 py-3"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-lg">{sportEmoji(code)}</span>
                          <span className="text-sm font-semibold text-slate-800">
                            {labelCapitalized || code}
                            {badge && <GenderBadge type={badge.type} />}
	                          </span>
	                        </div>
	                        <span className="text-sm font-medium text-slate-500">Access until: {subscriptionEnd}</span>
	                      </div>
	                    );
	                  })
	                ) : subscribedSports.length === 0 ? (
                  <div className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                    No subscribed sports found.
                  </div>
                ) : null}
              </div>
            )}
          </div>

          {/* Active Add-ons */}
          <div className="rounded-[28px] border border-slate-200 bg-white/95 p-6 shadow-[0_18px_60px_rgba(15,23,42,0.10)] backdrop-blur overflow-y-auto max-h-[600px]">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900">Active Add-ons</h2>
              <button
                type="button"
                disabled={isLoading}
                onClick={() => {
                  // Build the AddonState from activeAddons to pre-select in Addons page
                  const addonState: Record<string, { statCrew: boolean; seasons: string[] }> = {};
                  activeAddons.forEach((addon) => {
                    const sportId = SPORT_CODE_TO_SUBSCRIPTION_ID[addon.sport_code || ""];
                    if (sportId) {
                      addonState[sportId] = {
                        statCrew: true,
                        seasons: [],
                      };
                    }
                  });
                  // Build selectedSports from subscribed sports
                  const selectedSportsList = subscribedSports.map((sport) => {
                    const code = getSportCode(sport);
                    return {
                      id: SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase(),
                      name: sport.name || code,
                      icon: sportEmoji(code),
                      gender: sportGenderBadge(code)?.type || null,
                    };
                  });
                  // Build basePriceBySportId from subscribed sports
                  const basePriceBySportId: Record<string, number> = {};
                  subscribedSports.forEach((sport) => {
                    const code = getSportCode(sport);
                    const sportId = SPORT_CODE_TO_SUBSCRIPTION_ID[code] || code.toLowerCase();
                    // Coming from Profile "Add more" is addon-only; keep base sports cost at 0.
                    basePriceBySportId[sportId] = 0;
                  });
                  navigate(ADDONS, {
                    state: {
                      selectedSports: selectedSportsList,
                      addons: addonState,
                      basePriceBySportId,
                      isAddonOnly: true, // Coming from Profile - only addons should be charged
                    },
                  });
                }}
                className="rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-red-50"
              >
                Add more
              </button>
            </div>

            {isLoading ? (
              <div className="flex min-h-[220px] items-center justify-center rounded-2xl bg-slate-50 px-4 py-6">
                <Spinner size="md" />
              </div>
            ) : Object.values(groupedAddonsBySport).length > 0 ? (
              <div className="space-y-4">
                {Object.values(groupedAddonsBySport).some((g) => g.isGrace && g.graceEnd && g.graceSeasons.length > 0) && (
                  <div className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                    <div className="mb-2 flex items-center justify-between">
                      <span className="text-xs font-extrabold uppercase tracking-wider text-amber-800">
                        Grace Access
                      </span>
                      <span className="text-[11px] font-semibold text-amber-700">
                        Access until
                      </span>
                    </div>
                    <div className="space-y-2">
                      {Object.values(groupedAddonsBySport)
                        .filter((g) => g.isGrace && g.graceEnd && g.graceSeasons.length > 0)
                        .map((g, idx) => {
                          const graceEnd = formatDateYYYYMMDD(String(g.graceEnd || ""));
                          return (
                            <div
                              key={`addon-grace-${g.sportCode}-${g.addonName}-${idx}`}
                              className="rounded-xl bg-white/70 px-3 py-2"
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2 min-w-0">
                                  <span className="text-base mt-0.5">{g.emoji}</span>
                                  <div className="min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-semibold text-slate-900 truncate">{g.sportName}</span>
                                      <span className="text-[11px] font-semibold text-slate-600 truncate">({g.addonName})</span>
                                    </div>
                                    <div className="mt-0.5 text-xs text-slate-700">
                                      Seasons: {g.graceSeasons.map((s) => s.year).join(", ")}
                                    </div>
                                  </div>
                                </div>
                                <span className="text-sm font-semibold text-amber-900 whitespace-nowrap">{graceEnd}</span>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                {Object.values(groupedAddonsBySport).filter((g) => g.seasons.length > 0).map((g, idx) => {
                  const seasonCount = g.seasons.length;
                  const visibleSeasons = getVisibleSeasons(g, `${g.sportCode}-${g.addonName}-${idx}`);
                  const hasMore = seasonCount > 2;
                  const isExpanded = expandedAddons[`${g.sportCode}-${g.addonName}`] || false;
                  return (
                    <div key={`${g.sportCode}-${g.addonName}-${idx}`} className="rounded-2xl bg-slate-50 px-4 py-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{g.emoji}</span>
                          <span className="text-sm font-bold text-slate-900">{g.sportName}</span>
                          <span className="text-xs text-slate-500">({g.addonName})</span>
                        </div>
                        <span className="text-xs text-slate-500">${g.costPerSeason} per season</span>
                      </div>
                      <div className="space-y-1">
                        {visibleSeasons.length > 0 && (
                          <div className="flex items-center justify-between text-sm text-slate-700">
                            <span>Season: {g.seasons.map(s => s.year).join(', ')}</span>
                          </div>
                        )}
                      </div>
                      <div className="mt-2 border-t border-slate-200 pt-2">
                        <div className="flex items-center justify-between text-sm font-semibold text-slate-900">
                          <span>Total ({seasonCount} season{seasonCount !== 1 ? 's' : ''})</span>
                          <span>${g.total}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
	              </div>
            ) : (
              <div className="rounded-2xl bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
                No active add-ons yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
